import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import axios from "axios";
import { Eyebrow, Reveal } from "./bits";
import { API, CATEGORY_TINTS, scrollToId } from "@/lib/site";

const EASE = [0.22, 1, 0.36, 1];

const tintFor = (category) => CATEGORY_TINTS[category] || "#E8EFF4";

const Chip = ({ children, tint }) => (
  <span
    className="inline-block rounded-full px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#10202E]"
    style={{ background: tint }}
  >
    {children}
  </span>
);

export const Journal = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const active = articles.find((a) => a.id === open);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((res) => setArticles(res.data.articles))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) {
      if (open) window.__lenis.stop();
      else window.__lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <section id="journal" data-testid="journal-section" className="relative py-28 md:py-40">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <Eyebrow>09 — Insights Journal</Eyebrow>
        </Reveal>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-8">
          <Reveal delay={0.08}>
            <h2 className="max-w-2xl font-display text-4xl leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              Thinking, <span className="italic text-emerald-600">out loud.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-base font-light leading-relaxed text-[#4E6A82]">
              Short, honest essays on money, goals and the behaviour in between.
            </p>
          </Reveal>
        </div>

        {loading ? (
          <div data-testid="journal-loading" className="mt-16 grid gap-5 lg:grid-cols-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-64 animate-pulse rounded-3xl border border-[#10202E]/[0.06] bg-white/70"
              />
            ))}
          </div>
        ) : articles.length === 0 ? (
          <p data-testid="journal-empty" className="mt-16 text-base font-light text-[#6E8698]">
            New essays are on their way.
          </p>
        ) : (
          <div className="mt-16 grid gap-5 lg:grid-cols-2">
            <Reveal className="h-full">
              <button
                type="button"
                data-testid={`journal-article-${articles[0].id.slice(0, 8)}`}
                onClick={() => setOpen(articles[0].id)}
                className="group flex h-full w-full flex-col justify-between rounded-3xl border border-[#10202E]/[0.08] bg-white p-9 text-left shadow-[0_2px_24px_rgba(16,32,46,0.05)] transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1.5 hover:border-emerald-600/30 hover:shadow-[0_20px_60px_rgba(16,32,46,0.1)]"
              >
                <div>
                  {articles[0].cover && (
                    <img
                      src={articles[0].cover}
                      alt=""
                      loading="lazy"
                      className="mb-6 h-52 w-full rounded-2xl object-cover"
                    />
                  )}
                  <Chip tint={tintFor(articles[0].category)}>{articles[0].category}</Chip>
                  <h3 className="mt-6 font-display text-3xl leading-snug text-[#10202E] sm:text-4xl">
                    {articles[0].title}
                  </h3>
                  <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-[#4E6A82]">
                    {articles[0].excerpt}
                  </p>
                </div>
                <div className="mt-10 flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8AA0B0]">
                    {articles[0].time}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-700">
                    Read
                    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-1" />
                  </span>
                </div>
              </button>
            </Reveal>

            <div className="flex flex-col gap-5">
              {articles.slice(1).map((a, i) => (
                <Reveal key={a.id} delay={0.08 + (i % 3) * 0.06} className="flex-1">
                  <button
                    type="button"
                    data-testid={`journal-article-${a.id.slice(0, 8)}`}
                    onClick={() => setOpen(a.id)}
                    className="group flex h-full w-full flex-col justify-between rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 text-left shadow-[0_2px_24px_rgba(16,32,46,0.05)] transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-emerald-600/30 hover:shadow-[0_16px_48px_rgba(16,32,46,0.1)]"
                  >
                    <div>
                      {a.cover && (
                        <img
                          src={a.cover}
                          alt=""
                          loading="lazy"
                          className="mb-5 h-32 w-full rounded-2xl object-cover"
                        />
                      )}
                      <div className="flex items-center justify-between gap-4">
                        <Chip tint={tintFor(a.category)}>{a.category}</Chip>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8AA0B0]">
                          {a.time}
                        </span>
                      </div>
                      <h3 className="mt-5 font-display text-2xl leading-snug text-[#10202E]">{a.title}</h3>
                      <p className="mt-3 text-sm font-light leading-relaxed text-[#4E6A82]">{a.excerpt}</p>
                    </div>
                    <span className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald-700">
                      Read
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-1" />
                    </span>
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="journal-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] overflow-y-auto bg-[#F8F7F3]/95 backdrop-blur-xl"
          >
            <div className="mx-auto max-w-2xl px-6 py-24">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="rounded-3xl border border-[#10202E]/[0.08] bg-white p-8 shadow-[0_24px_80px_rgba(16,32,46,0.12)] sm:p-12"
              >
                <div className="flex items-start justify-between gap-6">
                  <Chip tint={tintFor(active.category)}>{active.category}</Chip>
                  <button
                    type="button"
                    data-testid="journal-close"
                    onClick={() => setOpen(null)}
                    className="rounded-full border border-[#10202E]/15 p-2 text-[#10202E] transition-colors hover:border-emerald-600/50 hover:text-emerald-700"
                    aria-label="Close article"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                {active.cover && (
                  <img
                    src={active.cover}
                    alt=""
                    className="mt-6 max-h-64 w-full rounded-2xl object-cover"
                  />
                )}
                <h3 className="mt-6 font-display text-3xl leading-tight text-[#10202E] sm:text-4xl">
                  {active.title}
                </h3>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-[#8AA0B0]">
                  HRKAPITAL Journal · {active.time}
                </p>
                <div className="mt-8 space-y-6">
                  {active.body.map((p, i) => (
                    <p key={i} className="text-base font-light leading-relaxed text-[#33506A]">
                      {p}
                    </p>
                  ))}
                </div>
                <p className="mt-10 border-t border-[#10202E]/10 pt-6 font-mono text-[10px] leading-relaxed tracking-[0.12em] text-[#8AA0B0]">
                  EDUCATIONAL CONTENT ONLY — NOT INVESTMENT ADVICE. MUTUAL FUND INVESTMENTS ARE
                  SUBJECT TO MARKET RISKS.
                </p>
                <button
                  type="button"
                  data-testid="journal-cta"
                  onClick={() => {
                    setOpen(null);
                    setTimeout(() => scrollToId("contact"), 300);
                  }}
                  className="group mt-8 inline-flex items-center gap-2 rounded-full bg-emerald-400 px-6 py-3 text-sm font-semibold text-[#04120c] transition-colors duration-300 hover:bg-emerald-300"
                >
                  Start a conversation
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
